<template>
    <v-dialog v-model="internalOpen" width="560">
        <v-card flat>
            <!-- Header Section: White Background -->
            <v-card-title
                class="text-uppercase text-text"
                style="word-break: break-word"
            >
                {{ $t("searchtool.titulo") }}
                <div class="text-wrap text-textDarken text-body-2 mb-2">
                    {{ $t("searchtool.subtitulo") }}
                </div>
            </v-card-title>

            <!-- Search Field Section: Greyish Background -->
            <v-card-text class="py-4 px-6" style="background-color: #f2f2f3">
                <v-text-field
                    v-model="textoBuscar"
                    color="secondary"
                    variant="outlined"
                    append-inner-icon="mdi-magnify"
                    :label="$t('searchtool.etiqueta')"
                    :error-messages="
                        $v.textoBuscar.$errors.map((e) => e.$message)
                    "
                    clearable
                    density="compact"
                    ref="buscar"
                    @keydown.enter="buscarFn"
                    @click:clear="borrarBusqueda"
                    @click:append-inner="buscarFn"
                />
            </v-card-text>

            <!-- Results Section: Gray Background with White Results Container -->
            <v-card-text
                v-if="formas.length > 0 && nuevaBusqueda == 1"
                class="px-6 py-4"
                style="background-color: #f2f2f3; position: relative"
            >
                <!-- Results Count -->
                <div
                    class="text-secondary font-weight-medium mb-4"
                    style="font-size: 20px"
                >
                    {{ formas.length + " " + $t("searchtool.resultados") }}
                </div>
                <!-- White Results Container -->
                <div
                    class="white"
                    style="position: relative; background-color: #ffffff"
                >
                    <div
                        ref="cardText"
                        class="white py-2 pb-4"
                        style="max-height: 154px; overflow-y: auto"
                    >
                        <div v-for="(forma, index) in formas" :key="index">
                            <v-list-item class="white mb-2">
                                <v-list-item-title
                                    class="text-textDarken"
                                    style="
                                        white-space: normal;
                                        word-break: break-word;
                                        overflow-wrap: break-word;
                                        flex: 1;
                                        padding-right: 8px;
                                    "
                                    >{{ forma.nombre }}</v-list-item-title
                                >
                                <template v-slot:append>
                                    <BotonDinamico
                                        :forma="forma"
                                        :stand="standObj"
                                        origen="2"
                                        :key="forma.id"
                                        v-if="
                                            forma.contador_cierre > 0 ||
                                            forma.fecha_baja == null
                                        "
                                    >
                                        <template #default="{ attrs, accion }">
                                            <v-btn
                                                color="secondary"
                                                class="text-white"
                                                style="flex-shrink: 0"
                                                v-bind="attrs"
                                                @click.stop="accion"
                                                >{{ $t("boton-ver") }}</v-btn
                                            >
                                        </template>
                                    </BotonDinamico>
                                </template>
                            </v-list-item>
                        </div>
                    </div>
                    <v-btn
                        icon
                        v-if="formas.length > 3"
                        @click="scrollToBottom"
                        color="tertiary"
                        style="
                            position: absolute;
                            bottom: -20px;
                            right: 50%;
                            transform: translateX(50%);
                            z-index: 10;
                        "
                    >
                        <v-icon color="white">mdi-chevron-down</v-icon>
                    </v-btn>
                </div>
            </v-card-text>

            <!-- No Results Section: White Background -->
            <v-card-text
                v-if="formas.length == 0 && nuevaBusqueda == 1"
                class="px-6 white text-center py-6"
            >
                <div>
                    <v-icon x-large color="secondary"
                        >mdi-file-document-remove-outline</v-icon
                    >
                </div>
                <div class="text--text mt-4">
                    {{ $t("searchtool.sin-resultados") }} "{{ textoBuscar }}"
                </div>
            </v-card-text>

            <!-- Recent Searches Section: Greyish Background -->
            <v-card-text
                v-if="busquedas.length > 0"
                class="px-6 py-4"
                style="background-color: #f2f2f3"
            >
                <v-subheader class="text-secondary text-uppercase pl-0">{{
                    $t("searchtool.busquedas")
                }}</v-subheader>
                <v-chip-group active-class="text-primary">
                    <v-chip
                        v-for="(busqueda, index) in busquedas"
                        :key="index"
                        @click="buscarAnterior(busqueda.texto)"
                    >
                        {{ busqueda.texto }}
                    </v-chip>
                </v-chip-group>
            </v-card-text>

            <!-- Footer Section: White Background -->
            <v-card-actions class="white d-flex flex-row justify-center">
                <v-btn
                    variant="outlined"
                    color="secondary"
                    class="mx-2"
                    @click="cerrarBusqueda"
                >
                    {{ $t("boton-cerrar") }}
                </v-btn>
                <v-btn
                    variant="flat"
                    color="secondary"
                    class="mx-2"
                    @click="buscarFn"
                >
                    {{ $t("buscar") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { useI18n } from "vue-i18n";
import axios from "axios";
import BotonDinamico from "../../components/BotonDinamico.vue";

const props = defineProps({
    modelValue: { type: Boolean, required: true },
    stand: { type: Object, required: true },
});
const emit = defineEmits(["update:modelValue"]);

// reactivity bridge for v-model
const internalOpen = ref(props.modelValue);
watch(
    () => props.modelValue,
    (val) => (internalOpen.value = val)
);
watch(internalOpen, (val) => {
    emit("update:modelValue", val);
    if (val) initDialog();
});

// Data
const textoBuscar = ref(null);
const busquedas = ref([]);
const formas = ref([]);
const nuevaBusqueda = ref(0);

// template refs
const buscar = ref(null);
const cardText = ref(null);

// Validation
const { t } = useI18n();
const rules = {
    textoBuscar: {
        required: helpers.withMessage(
            () => t("validations.required"),
            required
        ),
    },
};
const $v = useVuelidate(rules, { textoBuscar });

const standObj = computed(() =>
    props.stand?.value ? props.stand.value : props.stand
);

function initDialog() {
    axios.post(route("buscador.recientes")).then((r) => {
        busquedas.value = r.data;
        $v.value.$reset();
        setTimeout(() => buscar.value?.focus(), 200);
    });
}

function buscarFn() {
    $v.value.textoBuscar.$touch();
    if ($v.value.textoBuscar.$invalid) return;
    axios
        .post(route("buscador"), {
            busqueda: textoBuscar.value,
            stand: standObj.value.id,
        })
        .then((r) => {
            formas.value = r.data;
            nuevaBusqueda.value = 1;
        });
}

function cerrarBusqueda() {
    textoBuscar.value = null;
    formas.value = [];
    nuevaBusqueda.value = 0;
    $v.value.$reset();
    internalOpen.value = false;
}
function borrarBusqueda() {
    textoBuscar.value = null;
    formas.value = [];
    nuevaBusqueda.value = 0;
    $v.value.$reset();
}
function buscarAnterior(texto) {
    textoBuscar.value = texto;
    buscarFn();
}
function scrollToBottom() {
    const el = cardText.value;
    if (el) el.scrollTop = el.scrollHeight;
}
</script>
